import React, { useEffect, useState } from 'react';
import {
  getLeadByID,
  getCloseDealsById,
  getAllProjects,
  addFollowUp,
  updateFollowUp,
  createClosedDeal,
  createUnderConsideration,
  updateStage,
  updateLead,
  cloneLead,
  deleteLead,
  handleCheckLeadDelete,
  getAllAccommodationsByProject,
  updateFollowUpDate
} from '../services/api/implementation/impl';
import Multiselect from 'multiselect-react-dropdown';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useForm, Controller } from 'react-hook-form';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  Calendar,
  Plus,
  X,
  Pencil,
  Info,
  MapPin,
  Flame,
  User,
  Receipt,
  MoveRight,
  Copy,
  Trash2
} from 'lucide-react';
import { ClosedWonCloseDeal } from '../components/forms/ClosedWonCloseDeal';
import { UnderConsiderationCreate } from '../components/forms/UnderConsiderationCreate';
import EditLeadForm from '../components/forms/EditLeadForm';

function PricingDetailsSection({ title, subtitle, children }) {
  return (
    <div className="px-2 md:px-4 mb-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
        <div className="border-b border-slate-100/80 bg-gradient-to-r from-teal-50 via-white to-slate-50 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-700/12 text-teal-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
              aria-hidden>
              <Receipt className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
              {subtitle ? (
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{subtitle}</p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6">{children}</div>
      </div>
    </div>
  );
}

function PricingMetricTile({ label, value, emphasis, labelActions }) {
  return (
    <div
      className={
        emphasis
          ? 'relative flex min-h-[7.5rem] flex-col justify-between rounded-xl border border-teal-200/90 bg-gradient-to-br from-teal-50/90 via-white to-teal-50/40 p-4 shadow-[0_2px_16px_rgba(15,118,110,0.1)] ring-1 ring-teal-800/10'
          : 'relative flex min-h-[6.5rem] flex-col justify-between rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all hover:border-slate-300/90 hover:shadow-md'
      }>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {labelActions ? <div className="shrink-0">{labelActions}</div> : null}
      </div>
      <p
        className={`mt-3 tabular-nums font-semibold text-slate-900 ${
          emphasis ? 'text-xl text-teal-900 sm:text-2xl' : 'text-base sm:text-lg'
        }`}>
        {value}
      </p>
    </div>
  );
}

const SaleOverview = ({
  leadID,
  type,
  activeHeaderTab = 'Overview',
  onHeaderTabChange,
  combineHistoryAndLogsTabs = false,
  dealPricingDetailsToolbar = null
}) => {
  /* 
    Header data
  */

  const { id } = useParams();
  const navigate = useNavigate();
  const userId = Cookies.get('userId');

  const ID = leadID ? leadID : id;

  // Follow up modal states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [log, setLog] = useState('');
  const [stage, setStage] = useState('');
  const [agenda, setAgenda] = useState('');
  const [otherLog, setOtherLog] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedStage, setSelectedStage] = useState('');
  const [isCloseDealModalOpen, setIsCloseDealModalOpen] = useState(false);
  const [isUnderConsiderationModalOpen, setIsUnderConsiderationModalOpen] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [stageToUpdate, setStageToUpdate] = useState('');
  const [isUpdatingStage, setIsUpdatingStage] = useState(false);
  const [ucDetails, setUcDetails] = useState({
    project: null,
    block: null,
    level: null,
    unit: null
  });
  const [projectAccommodations, setProjectAccommodations] = useState([]);

  // Follow up modal states
  const [showUpdateFollowUpModal, setShowUpdateFollowUpModal] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  const [isUpdatingFollowUp, setIsUpdatingFollowUp] = useState(false);
  const [showExtraChargesBreakdownModal, setShowExtraChargesBreakdownModal] = useState(false);
  const [isEditLeadModalOpen, setIsEditLeadModalOpen] = useState(false);
  const [isUpdatingLeadDetails, setIsUpdatingLeadDetails] = useState(false);
  const [isCloneLeadModalOpen, setIsCloneLeadModalOpen] = useState(false);
  const [selectedCloneProjects, setSelectedCloneProjects] = useState([]);
  const [canDelete, setCanDelete] = useState(null);
  const [deleteResData, setDeleteResData] = useState(null);
  const [isDeletingLead, setIsDeletingLead] = useState(false);
  const queryClient = useQueryClient();

  const {
    control,
    setValue,
    formState: { errors }
  } = useForm();

  // Stage options from Leads.jsx
  const stageOptions = [
    { id: 'Phone Call Initiated', title: 'Phone Call Initiated (Cold)' },
    { id: 'Approached', title: 'Approached (Cold)' },
    { id: 'Presentation', title: 'Presentation (Warm)' },
    { id: 'Site Visited', title: 'Site Visited (Warm)' },
    { id: 'Commercials Shared', title: 'Commercials Shared (Hot)' },
    { id: 'Negotiation', title: 'Negotiation (Hot)' },
    { id: 'Under Consideration', title: 'Under Consideration (Confirmed)' },
    { id: 'Deal Closed', title: 'Deal Closed (Closed Won)' },
    { id: 'Deal Dropped', title: 'Deal Dropped (Closed Lost)' }
  ];

  // Log options from Leads.jsx
  const logOptions = [
    'Call Not Connected',
    'Switched Off',
    'Not Reachable',
    'Number Busy',
    'No Answer',
    'Wrong Number',
    'Call Connected (Not Interested)',
    'Call Connected (Interested)',
    'Call Connected (Call Back Later)',
    'Call Connected (Sent WhatsApp)',
    'Call Connected (Sent SMS)',
    'Call Connected (Shared Brochure)',
    'Call Connected (Meeting Scheduled)',
    'Call Connected (Shared Pricing Info)',
    'Call Connected (Language Barrier)',
    'Call Connected (Decision Maker Not Available)',
    'Follow-up Tomorrow',
    'Follow-up in 3 Days',
    'Follow-up Next Week',
    'Follow-up After Site Visit',
    'Follow-up After Family Discussion',
    'Follow-up After Festival/Holiday',
    'Awaiting Callback from Customer',
    'Looking for Plot',
    'Looking for Flat',
    'Looking for Commercial',
    'Finalizing Options',
    'Just Exploring',
    'Budget Too Low',
    'Referred by Friend/Family',
    'Wants Site Visit',
    'Site Visit Done (Awaiting Feedback)',
    'Site Visit Done (Not Interested)',
    'Booked with Another Project',
    'Booked with Us',
    'Already Visited Office',
    'Project Not in Preferred Locality',
    'Other'
  ];

  // Track component mount

  // Track page view
  useEffect(() => {
    mixpanel.track('Sale Overview Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    mixpanel.track('Sale Overview Viewed', {
      user_id: userId,
      lead_id: ID,
      timestamp: new Date().toISOString()
    });
  }, [ID]);

  const {
    data: leadbyId,
    isLoading,
    error,
    refetch: refetchLeadbyId
  } = useQuery({
    queryKey: ['leadById', parseInt(ID)],
    queryFn: async () => {
      const res = await getLeadByID(parseInt(ID));
      return res;
    },
    enabled: !!ID, // Only run the query if id is truthy
    onSuccess: (data) => {
      mixpanel.track('Sale Overview Lead API Success', {
        user_id: userId,
        lead_id: ID,
        lead_name: data?.user?.name,
        lead_status: data?.pstatus,
        assigned_agent: data?.sales?.name,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Sale Overview Lead API Failed', {
        user_id: userId,
        lead_id: ID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const dealIdForCloseDeal =
    leadbyId?.deal?.id ?? leadbyId?.activities?.find((a) => a.name === 'Deal Closed')?.refrence_id;
  const { data: closeDealData } = useQuery({
    queryKey: ['closeDealsById', dealIdForCloseDeal],
    queryFn: () => getCloseDealsById(dealIdForCloseDeal),
    enabled: type === 'deal' && !!dealIdForCloseDeal
  });

  const { data: projects } = useQuery({
    queryKey: ['allProjectsForLeadEdit'],
    queryFn: getAllProjects
  });

  useEffect(() => {
    refetchLeadbyId();
  }, [leadbyId]);

  const getLeadSourceLabel = (lead) => {
    if (!lead?.preLead) return lead?.source || '---';
    const campaignName = lead?.preLead?.interested_campaign?.name?.trim() || '---';

    return campaignName || lead?.source || '---';
  };

  // Update ucDetails when leadbyId changes (similar to CustomerList.jsx)
  useEffect(() => {
    if (leadbyId?.underConsideration) {
      const blockId = leadbyId?.underConsideration?.unit?.block_id;
      const levelId = leadbyId?.underConsideration?.unit?.level_id;
      const unitId = leadbyId?.underConsideration?.unit?.id;
      const projectId = leadbyId?.underConsideration?.unit?.project_id;
      setUcDetails({ project: projectId, block: blockId, level: levelId, unit: unitId });
    } else {
      setUcDetails({ project: null, block: null, level: null, unit: null });
    }
  }, [leadbyId]);

  // Fetch project accommodations based on lead's project
  const { data: projectAccommodationsData } = useQuery({
    queryKey: ['project_accommodations', leadbyId?.interested_projects?.[0]],
    queryFn: () => getAllAccommodationsByProject(leadbyId?.interested_projects?.[0]),
    enabled: !!leadbyId?.interested_projects?.[0],
    onError: (error) => {
      console.error('Error fetching project accommodations:', error);
    }
  });

  // Prefill project accommodations from lead data when available
  useEffect(() => {
    if (
      leadbyId?.accomodations &&
      Array.isArray(leadbyId.accomodations) &&
      projectAccommodationsData &&
      projectAccommodationsData.length > 0
    ) {
      const accommodationIds = leadbyId.accomodations;
      const initialAccommodations = projectAccommodationsData.filter((acc) =>
        accommodationIds.includes(acc.id)
      );
      setProjectAccommodations(initialAccommodations);
    } else if (leadbyId && projectAccommodationsData) {
      // Reset if no accommodations in lead
      setProjectAccommodations([]);
    }
  }, [leadbyId, projectAccommodationsData]);

  function formatDate(dateString) {
    if (!dateString) return '';

    const date = new Date(dateString);

    if (isNaN(date?.getTime())) return '---';

    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date?.toLocaleDateString('en-GB', options); // e.g., 01 May 2025
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '₹0';
    return `₹${parseInt(amount).toLocaleString('en-IN')}`;
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'hot':
        return 'bg-orange-100 text-orange-900';
      case 'warm':
        return 'bg-amber-100 text-amber-900';
      case 'cold':
        return 'bg-blue-100 text-blue-900';
      case 'deal closed':
        return 'bg-green-100 text-green-900';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'deal':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'underConsideration':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const showStatusChip = leadbyId?.pstatus?.toLowerCase();
  const showTypeChip = type && type !== 'underConsideration';
  const primaryPhone = leadbyId?.user?.phone_number || '';
  const stageFlow = [
    'Lead Created',
    'Approached',
    'Phone Call Initiated',
    'Presentation',
    'Site Visited',
    'Commercials Shared',
    'Negotiation',
    'Under Consideration',
    'Partner Approval',
    'Management Approval',
    'Management Denied',
    'Application Form',
    'Allotment Letter',
    'Demand Note (Invoice)',
    'Payment Stage (Invoice)',
    'Invoice Advance',
    'Aggrement Letter',
    'Token Advance',
    'Change Request',
    'Change Request Invoice',
    'Extra Charge Claim',
    'Invoice Sent',
    'Gentle Reminder',
    'First Delay Follow-Up',
    'Second Delay Follow-Up',
    'Final Reminder / Soft Escalation',
    'Hard Escalation',
    'Legal / Final Action',
    'Payment Committed Awaiting UTR',
    'Partial Payment Received',
    'Payment Under Processing',
    'Payment Received Closed',
    'Deal Closed',
    'Deal Dropped',
    'Deal Cancelled'
  ];
  const currentStage = leadbyId?.activities?.[0]?.name || '';
  const currentStageIndex = stageFlow.indexOf(currentStage);
  const latestFollowUpDate = leadbyId?.follow_ups?.[0]?.nextFollowUpDate || '-';
  const hasFollowUp = Boolean(leadbyId?.follow_ups?.[0]);
  const hasValidFollowUpDate = (() => {
    const rawDate = leadbyId?.follow_ups?.[0]?.nextFollowUpDate;
    if (!rawDate) return false;
    const parsedDate = new Date(rawDate);
    return !Number.isNaN(parsedDate.getTime());
  })();
  const nextAction =
    currentStageIndex >= 0 && currentStageIndex < stageFlow.length - 1
      ? stageFlow[currentStageIndex + 1]
      : '---';
  const basicDetails = {
    name: leadbyId?.user?.name || '---',
    phone: leadbyId?.user?.phone_number || '---',
    email: leadbyId?.user?.email || '---',
    city: leadbyId?.address?.city || '---',
    source: getLeadSourceLabel(leadbyId) || '---',
    purpose:
      leadbyId?.purpose ||
      leadbyId?.purchase_type ||
      leadbyId?.purchasePurpose ||
      leadbyId?.purchase ||
      '---'
  };
  const requirementDetails = {
    project: leadbyId?.projects?.[0]?.name || '---',
    type:
      leadbyId?.accomodations?.[0]?.name ||
      projectAccommodations?.[0]?.name ||
      leadbyId?.property_type ||
      '---',
    preferredUnit:
      leadbyId?.underConsideration?.unit?.unitName ||
      leadbyId?.underConsideration?.unit?.name ||
      leadbyId?.preferred_unit ||
      '---',
    budget: leadbyId?.expectedBudgetRange ? `${leadbyId.expectedBudgetRange} Lakhs` : '---',
    timeline: formatDate(leadbyId?.expected_clouser_date) || '---',
    interestedPlotArea:
      Array.isArray(leadbyId?.plotArea) && leadbyId.plotArea.length > 0
        ? leadbyId.plotArea.join(', ')
        : '---',
    interestedAccommodationType:
      Array.isArray(leadbyId?.interestedAccomodationType) &&
      leadbyId.interestedAccomodationType.length > 0
        ? leadbyId.interestedAccomodationType.join(', ')
        : '---'
  };
  const historyLogsTabs = combineHistoryAndLogsTabs ? ['Stages History & logs'] : ['Logs', 'Stage History'];
  const headerTabs =
    type === 'underConsideration'
      ? ['Overview', 'Pricing Details', ...historyLogsTabs]
      : type === 'deal'
        ? ['Overview', 'Pricing Details', ...historyLogsTabs]
        : ['Overview', ...historyLogsTabs];
  const salesJourneyItems = ((leadbyId?.activities || []).map((activity) => {
    const rawDate =
      activity?.created_at ||
      activity?.createdAt ||
      activity?.date ||
      activity?.updated_at ||
      activity?.updatedAt;
    return {
      title: activity?.name || activity?.title || 'Activity updated',
      subtitle: activity?.description || '',
      date: formatDate(rawDate) || '---',
      ts: rawDate ? new Date(rawDate).getTime() : 0
    };
  }) || [])
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 6);
  const salesLogItems = ((leadbyId?.sales_logs || []).map((log) => {
    const rawDate = log?.createdAt || log?.created_at || log?.updatedAt || log?.updated_at;
    const title = log?.name || 'Sales log updated';
    const subtitle = log?.description || '';
    return {
      title,
      subtitle: subtitle?.trim() === title?.trim() ? '' : subtitle,
      date: formatDate(rawDate) || '---',
      ts: rawDate ? new Date(rawDate).getTime() : 0
    };
  }) || [])
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 6);

  // Reset Follow Up Form
  const resetFollowUpForm = () => {
    setAgenda('');
    setLog('');
    setFollowUpDate('');
    setStage('');
    setOtherLog('');
    setUploadedImage(null);
    setProjectAccommodations([]);
    setValue('followUpStage', '');
    setSelectedStage('');
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = Cookies.get('token');
      if (!token) throw new Error('No token found');

      const formData = new FormData();
      formData.append('files', file);

      const res = await axios.post('https://application.housingram.com/api/s3/upload', formData, {
        headers: {
          authorization: token,
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploaded = res.data.data.files[0];
      setUploadedImage({
        name: uploaded.originalName,
        url: uploaded.presignedUrl
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Handle Complete Follow up
  const handleCompleteFollowup = async () => {
    if (!stage) {
      toast.error('Select Stage');
      return;
    }

    if (stage !== 'Deal Closed' && stage !== 'Deal Dropped' && !followUpDate) {
      toast.error('Select Next Follow Up Date');
      return;
    }

    if (stage !== 'Deal Closed' && stage !== 'Deal Dropped' && !agenda) {
      toast.error('Enter Agenda');
      return;
    }

    if (!log) {
      toast.error('Select/Enter log');
      return;
    }

    if (log === 'Other' && !otherLog) {
      toast.error('Enter log');
      return;
    }

    // Validate accommodation for specific stages
    if (
      (stage === 'Commercials Shared' || stage === 'Negotiation') &&
      projectAccommodations.length === 0
    ) {
      toast.error('Please select at least one project accommodation');
      return;
    }

    const existingFollowUpId = leadbyId?.follow_ups?.[0]?.id;
    const payload = {
      agenda,
      nextFollowUpDate: followUpDate,
      log: log === 'Other' ? otherLog : log,
      lead_id: ID,
      status: existingFollowUpId ? 'Inactive' : 'Active',
      name: stage,
      attachments: uploadedImage ? [{ name: uploadedImage.name, url: uploadedImage.url }] : []
    };

    console.log('Follow up payload', payload);

    try {
      setIsSubmitting(true);
      if (existingFollowUpId) {
        await updateFollowUp(existingFollowUpId, payload);
      } else {
        await addFollowUp(payload);
      }

      // Update lead accommodation if needed
      if (stage === 'Commercials Shared' || stage === 'Negotiation') {
        try {
          const leadPayload = {
            accomodations: projectAccommodations.map((item) => item.id || item)
          };
          await updateLead(ID, leadPayload);
          console.log('Lead project accommodation updated');
        } catch (leadError) {
          console.error('Failed to update lead project accommodation:', leadError);
          // Don't show error as follow-up was successful
        }
      }

      toast.success('Follow Up  successfully');
      await refetchLeadbyId();
      setShowFollowUpModal(false);
      resetFollowUpForm();

      // Navigate if needed
      if (selectedStage && selectedStage === 'Under Consideration') {
        setIsUnderConsiderationModalOpen(true);
      }

      if (selectedStage && selectedStage === 'Deal Closed') {
        setIsCloseDealModalOpen(true);
      }
    } catch (error) {
      console.error('Failed to update followup:', error);
      toast.error(error?.response?.data?.message || 'Failed to update followup');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open modal and prefill stage
  const handleOpenFollowUpModal = () => {
    const currentStage = leadbyId?.activities?.[0]?.name;
    if (currentStage && stageOptions?.find((item) => item.id === currentStage)) {
      setStage(currentStage);
      setValue('followUpStage', currentStage);
      setSelectedStage(currentStage);
    }

    // Prefill will be handled by useEffect when projectAccommodationsData loads
    setShowFollowUpModal(true);
  };

  const handleCloseDeal = (data) => {
    createClosedDealMutation(data);
  };

  const { mutate: createClosedDealMutation } = useMutation({
    mutationFn: (data) => createClosedDeal(data),
    onSuccess: () => {
      toast.success('Deal closed successfully');
      setIsCloseDealModalOpen(false);
      refetchLeadbyId();
    },
    onError: (error) => {
      toast.error(error.response.data.message || error?.message || 'Failed to close deal');
    }
  });

  const handleUnderConsideration = (data) => {
    createUnderConsiderationMutation(data);
  };

  const { mutate: createUnderConsiderationMutation } = useMutation({
    mutationFn: (data) => createUnderConsideration(data),
    onSuccess: () => {
      toast.success('Under Consideration created successfully');
      setIsUnderConsiderationModalOpen(false);
      refetchLeadbyId();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to create under consideration'
      );
    }
  });

  // Handle stage update
  const handleUpdateStage = async () => {
    if (!stageToUpdate) {
      toast.error('Please select a stage');
      return;
    }

    // Validate accommodation for specific stages
    if (
      (stageToUpdate === 'Commercials Shared' || stageToUpdate === 'Negotiation') &&
      projectAccommodations.length === 0
    ) {
      toast.error('Please select at least one project accommodation');
      return;
    }

    const payload = {
      name: stageToUpdate,
      lead_id: ID
    };

    console.log('Update stage payload:', payload);

    if (stageToUpdate === 'Deal Closed') {
      setShowStageModal(false);
      setIsCloseDealModalOpen(true);
      return;
    }

    if (stageToUpdate === 'Under Consideration') {
      setShowStageModal(false);
      setIsUnderConsiderationModalOpen(true);
      return;
    }

    try {
      setIsUpdatingStage(true);
      await updateStage(payload);

      // Update lead accommodation if needed
      if (stageToUpdate === 'Commercials Shared' || stageToUpdate === 'Negotiation') {
        try {
          const leadPayload = {
            accomodations: projectAccommodations.map((item) => item.id || item)
          };
          await updateLead(ID, leadPayload);
          console.log('Lead project accommodation updated');
        } catch (leadError) {
          console.error('Failed to update lead project accommodation:', leadError);
          // Don't show error as stage update was successful
        }
      }

      toast.success('Stage updated successfully');
      setShowStageModal(false);
      setStageToUpdate('');
      setProjectAccommodations([]);
      await refetchLeadbyId();
    } catch (error) {
      console.error('Failed to update stage:', error);
      toast.error(error?.response?.data?.message || 'Failed to update stage');
    } finally {
      setIsUpdatingStage(false);
    }
  };

  const handleUpdateFollowUpDate = async () => {
    if (!selectedFollowUp) return;
    if (!followUpDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      setIsUpdatingFollowUp(true);
      const payload = {
        nextFollowUpDate: new Date(followUpDate).toISOString()
        // agenda: selectedFollowUp.agenda // Keep existing agenda
      };

      await updateFollowUpDate(selectedFollowUp.id, payload);
      toast.success('Follow up date updated successfully');
      setShowUpdateFollowUpModal(false);
      setSelectedFollowUp(null);
      setFollowUpDate('');
      refetchLeadbyId();
    } catch (error) {
      console.error('Failed to update follow up date:', error);
      toast.error(error?.response?.data?.message || 'Failed to update follow up date');
    } finally {
      setIsUpdatingFollowUp(false);
    }
  };

  const handleEditLeadSubmit = async (payload) => {
    try {
      setIsUpdatingLeadDetails(true);
      await updateLead(ID, payload);
      toast.success('Lead updated successfully');
      setIsEditLeadModalOpen(false);
      await refetchLeadbyId();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update lead');
    } finally {
      setIsUpdatingLeadDetails(false);
    }
  };

  const { mutate: cloneLeadMutation, isPending: isCloningLead } = useMutation({
    mutationFn: (payload) => cloneLead(ID, payload),
    onMutate: (data) => {
      mixpanel.track('Duplicate Lead Started', {
        user_id: userId,
        lead_id: ID,
        project_ids: data.project_ids,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: (result, variables) => {
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Duplicate Lead Failed', {
          user_id: userId,
          error_message:
            result?.response?.data?.message || result?.message || 'Failed to duplicate lead',
          lead_id: ID,
          project_ids: variables.project_ids,
          timestamp: new Date().toISOString()
        });
        toast.error(
          result?.response?.data?.message || result?.message || 'Failed to duplicate lead'
        );
        return;
      }

      mixpanel.track('Duplicate Lead Success', {
        user_id: userId,
        lead_id: ID,
        project_ids: variables.project_ids,
        timestamp: new Date().toISOString()
      });

      toast.success('Lead cloned successfully');
      setIsCloneLeadModalOpen(false);
      setSelectedCloneProjects([]);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (error, variables) => {
      mixpanel.track('Duplicate Lead Failed', {
        user_id: userId,
        error_message:
          error?.response?.data?.message || error?.message || 'Failed to duplicate lead',
        lead_id: ID,
        project_ids: variables?.project_ids,
        timestamp: new Date().toISOString()
      });
      toast.error(error?.response?.data?.message || error?.message || 'Failed to duplicate lead');
    }
  });

  const handleOpenCloneLeadModal = () => {
    mixpanel.track('Duplicate Lead Modal Opened', {
      user_id: userId,
      lead_id: ID,
      lead_name: leadbyId?.user?.name,
      timestamp: new Date().toISOString()
    });
    setSelectedCloneProjects([]);
    setIsCloneLeadModalOpen(true);
  };

  const handleSubmitCloneLead = () => {
    if (selectedCloneProjects.length === 0) {
      toast.error('Please select at least one project');
      return;
    }

    cloneLeadMutation({
      project_ids: selectedCloneProjects.map((project) => parseInt(project.id, 10))
    });
  };

  const cloneProjectOptions =
    projects
      ?.filter((project) => {
        const currentProjectId = leadbyId?.interested_projects?.[0];
        return currentProjectId ? project.id !== currentProjectId : true;
      })
      ?.map((project) => ({
        id: project.id.toString(),
        name: project.name
      })) || [];

  const navigateToLeadsList = () => {
    const projectID = leadbyId?.interested_projects?.[0] ?? null;
    navigate('/builder/sales/', {
      state: {
        secondaryTab: 'Leads',
        ...(projectID ? { projectID } : {})
      }
    });
  };

  const { mutate: checkDeleteLead } = useMutation({
    mutationFn: () => handleCheckLeadDelete(ID),
    onMutate: () => setIsDeletingLead(true),
    onSuccess: (result) => {
      setIsDeletingLead(false);
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        toast.error(
          result?.response?.data?.message || result?.message || 'Error checking dependencies'
        );
        return;
      }

      const leadName = leadbyId?.user?.name || 'Lead';
      if (result.canDelete) {
        setDeleteResData({ name: leadName });
        setCanDelete('yes');
      } else {
        setDeleteResData({ ...result, name: leadName });
        setCanDelete('no');
      }
    },
    onError: (error) => {
      setIsDeletingLead(false);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error checking dependencies'
      );
    }
  });

  const { mutate: deleteLeadMutation } = useMutation({
    mutationFn: (payload) => deleteLead(payload),
    onMutate: () => setIsDeletingLead(true),
    onSuccess: async (result) => {
      setIsDeletingLead(false);
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        toast.error(result?.response?.data?.message || result?.message || 'Error deleting lead');
        return;
      }

      toast.success('Lead deleted successfully');
      setCanDelete(null);
      setDeleteResData(null);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigateToLeadsList();
    },
    onError: (error) => {
      setIsDeletingLead(false);
      toast.error(error?.response?.data?.message || error?.message || 'Error deleting lead');
    }
  });

  const handleDeleteLeadClick = () => {
    mixpanel.track('Delete Lead Clicked', {
      user_id: userId,
      lead_id: ID,
      lead_name: leadbyId?.user?.name,
      timestamp: new Date().toISOString()
    });
    checkDeleteLead();
  };

  const handleConfirmDeleteLead = () => {
    deleteLeadMutation({ ids: [parseInt(ID, 10)] });
  };

  return (
    <div className="overflow-y-auto p-4 bg-gray-50 w-full">
      <div className="mx-auto max-w-[1500px] flex flex-col gap-2">
        {/* Header Section */}
        <div className="px-2 md:px-4 mb-2">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-semibold text-sm flex-shrink-0">
                {leadbyId?.user?.name?.charAt(0)?.toUpperCase() || 'L'}
              </div>
              <p className="font-bold text-gray-900 text-2xl md:text-[30px] leading-tight">
                {leadbyId?.user?.name || '---'}
              </p>
              <span className="text-gray-500 text-base font-medium">#{ID || '---'}</span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-sm leading-none font-semibold">
                <MapPin size={12} />
                {leadbyId?.activities?.[0]?.name || 'Stage Pending'}
              </span>
              {showStatusChip && (
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm leading-none capitalize font-semibold ${getStatusColor(leadbyId?.pstatus)}`}>
                  <Flame size={12} />
                  {leadbyId?.pstatus || '---'}
                </span>
              )}
              {!leadbyId?.activities?.some(
                (activity) => activity?.name === 'Management Approval'
              ) && (
                <button
                  type="button"
                  title="Move Stage"
                  aria-label="Move Stage"
                  onClick={() => {
                    setStageToUpdate(leadbyId?.activities?.[0]?.name || '');
                    setShowStageModal(true);
                  }}
                  className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors">
                  <MoveRight className="w-4 h-4" strokeWidth={2.25} />
                </button>
              )}
              <button
                type="button"
                title="Edit Lead"
                aria-label="Edit Lead"
                onClick={() => setIsEditLeadModalOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors">
                <Pencil className="w-4 h-4" strokeWidth={2.25} />
              </button>
              {type !== 'deal' && (
                <>
                  <button
                    type="button"
                    title="Duplicate Lead"
                    aria-label="Duplicate Lead"
                    onClick={handleOpenCloneLeadModal}
                    className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors">
                    <Copy className="w-4 h-4" strokeWidth={2.25} />
                  </button>
                  <button
                    type="button"
                    title="Delete Lead"
                    aria-label="Delete Lead"
                    onClick={handleDeleteLeadClick}
                    disabled={isDeletingLead}
                    className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:pointer-events-none">
                    <Trash2 className="w-4 h-4" strokeWidth={2.25} />
                  </button>
                </>
              )}
              {showTypeChip && (
                <span
                  className={`px-3 py-1.5 rounded-md border text-sm leading-none capitalize font-semibold ${getTypeBadgeColor(type)}`}>
                  {type}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-base text-gray-700 pl-0.5">
              {primaryPhone && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{primaryPhone}</span>
                </span>
              )}
              {leadbyId?.source && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{leadbyId.source}</span>
                </span>
              )}
              {leadbyId?.projects?.[0]?.name && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{leadbyId.projects[0].name}</span>
                </span>
              )}
              {leadbyId?.expectedBudgetRange && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">{`${leadbyId.expectedBudgetRange} Lakhs`}</span>
                </span>
              )}
              {leadbyId?.follow_ups?.[0]?.nextFollowUpDate && (
                <span className="flex items-center gap-2">
                  <span className="text-gray-400">•</span>
                  <span className="font-medium">
                    Follow up: {formatDate(leadbyId.follow_ups[0].nextFollowUpDate)}
                  </span>
                </span>
              )}
              {leadbyId?.sales?.name && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 text-violet-800 text-sm">
                  <User size={13} />
                  <span className="font-semibold">Sales Person:</span>
                  <span className="font-medium">{leadbyId.sales.name}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-2 md:px-4 mb-2 border-b border-gray-200">
          <div className="flex items-center gap-5 overflow-x-auto whitespace-nowrap">
            {headerTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => onHeaderTabChange?.(tab)}
                className={`text-base pb-2.5 ${
                  tab === activeHeaderTab
                    ? 'text-teal-700 border-b-2 border-teal-700 font-semibold'
                    : 'text-gray-700 font-medium hover:text-gray-900'
                }`}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        {activeHeaderTab === 'Overview' && (
          <>
            <div className="px-2 md:px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">Stage</p>
                <p className="text-base text-teal-700 font-semibold mt-1">
                  {leadbyId?.activities?.[0]?.name || '---'}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">Next Follow-up</p>
                <p className="text-base text-gray-900 font-semibold mt-1">
                  {formatDate(latestFollowUpDate) || '---'}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">Budget</p>
                <p className="text-base text-gray-900 font-semibold mt-1">
                  {leadbyId?.expectedBudgetRange ? `${leadbyId.expectedBudgetRange} Lakhs` : '---'}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">Next Action</p>
                <p className="text-base text-gray-900 font-semibold mt-1">{nextAction}</p>
              </div>
            </div>

            {/* Information Cards - Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-2 px-2 md:px-4">
              {/* Basic Details */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 uppercase tracking-wide">
                  Basic Details
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-sm font-semibold text-gray-900">{basicDetails.name}</p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="text-sm font-semibold text-teal-700">{basicDetails.phone}</p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-sm font-semibold text-gray-900">{basicDetails.email}</p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">City</p>
                    <p className="text-sm font-semibold text-gray-900">{basicDetails.city}</p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Source</p>
                    <p className="text-sm font-semibold text-gray-900">{basicDetails.source}</p>
                  </div>
                </div>
              </div>

              {/* Sales Journey */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-base md:text-lg font-semibold tracking-wide text-gray-900 mb-4">
                  SALES JOURNEY
                </h3>
                {salesJourneyItems.length > 0 ? (
                  <div className="space-y-2.5">
                    {salesJourneyItems.map((item, index) => (
                      <div key={`${item.title}-${index}`} className="grid grid-cols-[14px_120px_1fr] gap-3">
                        <span className="w-3 h-3 rounded-full bg-primary mt-1" />
                        <p className="text-gray-800 text-sm font-medium">{item.date}</p>
                        <div>
                          <p className="text-gray-900 text-sm font-medium">{item.title}</p>
                          {item.subtitle ? (
                            <p className="text-gray-600 text-xs leading-5">{item.subtitle}</p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm md:text-base">No sales journey available yet.</p>
                )}
              </div>

              {/* Next Follow-up Card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-lg md:text-xl font-semibold tracking-wide text-gray-900 mb-4">
                  NEXT FOLLOW-UP
                </h3>
                {hasFollowUp ? (
                  <>
                    <div className="space-y-3 text-sm md:text-base">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-gray-800">Date</p>
                        <p className="text-emerald-700 font-medium">
                          {formatDate(leadbyId?.follow_ups?.[0]?.nextFollowUpDate) || '---'}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-gray-800">Agenda</p>
                        <p className="text-gray-900 font-medium text-right">
                          {leadbyId?.follow_ups?.[0]?.agenda || '---'}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-gray-800">Mode</p>
                        <p className="text-gray-900 font-medium text-right">
                          {leadbyId?.follow_ups?.[0]?.mode ||
                            leadbyId?.follow_ups?.[0]?.log ||
                            '---'}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-gray-800">Owner</p>
                        <p className="text-gray-900 font-medium text-right">
                          {leadbyId?.sales?.name || 'Unassigned'}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 flex items-center gap-5 flex-wrap">
                      <button
                        type="button"
                        onClick={handleOpenFollowUpModal}
                        className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
                        Mark Done
                      </button>
                      {hasValidFollowUpDate && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFollowUp(leadbyId.follow_ups[0]);
                            const date = new Date(leadbyId.follow_ups[0].nextFollowUpDate);
                            const formattedDate = date.toISOString().slice(0, 16);
                            setFollowUpDate(formattedDate);
                            setShowUpdateFollowUpModal(true);
                          }}
                          className="text-sm md:text-base text-gray-900 hover:text-gray-700">
                          Reschedule
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-4">
                    <p className="text-gray-600 text-sm md:text-base">
                      No follow-up scheduled yet.
                    </p>
                    <div>
                      <button
                        type="button"
                        onClick={handleOpenFollowUpModal}
                        className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
                        Schedule Follow-up
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Requirement */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 uppercase tracking-wide">
                  Requirement
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Project</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {requirementDetails.project}
                    </p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Type</p>
                    <p className="text-sm font-semibold text-gray-900">{requirementDetails.type}</p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Budget</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {requirementDetails.budget}
                    </p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Interested Plot Area</p>
                    <p className="text-sm font-semibold text-gray-900 text-right max-w-[62%]">
                      {requirementDetails.interestedPlotArea}
                    </p>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                    <p className="text-sm text-gray-600">Interested Accommodation</p>
                    <p className="text-sm font-semibold text-gray-900 text-right max-w-[62%]">
                      {requirementDetails.interestedAccommodationType}
                    </p>
                  </div>
                  {leadbyId?.dates?.length === 1 ? (
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">Expected Closure Date</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatDate(leadbyId?.dates?.[0]?.date) || '---'}
                      </p>
                    </div>
                  ) : leadbyId?.dates?.length > 1 ? (
                    <>
                      <div className="flex justify-between items-center border-b border-gray-100 pb-1">
                        <p className="text-sm text-gray-600">Previous Closure Date</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(leadbyId?.dates?.[1]?.date) || '---'}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-600">Next Closure Date</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(leadbyId?.dates?.[0]?.date) || '---'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">Expected Closure Date</p>
                      <p className="text-sm font-semibold text-gray-900">---</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-base md:text-lg font-semibold tracking-wide text-gray-900 mb-4">
                  SALES LOG
                </h3>
                {salesLogItems.length > 0 ? (
                  <div className="space-y-2.5">
                    {salesLogItems.map((item, index) => (
                      <div key={`${item.title}-${index}`} className="grid grid-cols-[14px_120px_1fr] gap-3">
                        <span className="w-3 h-3 rounded-full bg-primary mt-1" />
                        <p className="text-gray-800 text-sm font-medium">{item.date}</p>
                        <div>
                          <p className="text-gray-900 text-sm font-medium">{item.title}</p>
                          {item.subtitle ? (
                            <p className="text-gray-600 text-xs leading-5">{item.subtitle}</p>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-sm md:text-base">No sales logs available yet.</p>
                )}
              </div>
            </div>
          </>
        )}

        {leadbyId && type === 'deal' && activeHeaderTab === 'Pricing Details' && (
          <>
            {dealPricingDetailsToolbar}
            <PricingDetailsSection
              title="Pricing details"
              subtitle="Commercial snapshot from the closed deal — unit, PLC, extras, GST, and total.">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <PricingMetricTile label="Unit price" value={formatCurrency(leadbyId?.deal?.unit_price)} />
                <PricingMetricTile label="PLC price" value={formatCurrency(leadbyId?.deal?.plc_price)} />
                <PricingMetricTile
                  label="Extra charges"
                  value={formatCurrency(leadbyId?.deal?.extra_charges)}
                  labelActions={
                    (closeDealData?.extra_charges_snapshot?.length > 0 ||
                      leadbyId?.deal?.extra_charges_snapshot?.length > 0) ? (
                      <button
                        type="button"
                        onClick={() => setShowExtraChargesBreakdownModal(true)}
                        className="rounded-md p-1 text-teal-800/80 transition-colors hover:bg-teal-100 hover:text-teal-900"
                        title="View breakdown">
                        <Info size={16} strokeWidth={2} aria-hidden />
                        <span className="sr-only">View extra charges breakdown</span>
                      </button>
                    ) : null
                  }
                />
                <PricingMetricTile label="GST" value={formatCurrency(leadbyId?.deal?.gst_price)} />
                <PricingMetricTile
                  label="Total price"
                  value={formatCurrency(leadbyId?.deal?.total_price)}
                  emphasis
                />
              </div>
            </PricingDetailsSection>
          </>
        )}

        {leadbyId && type === 'underConsideration' && activeHeaderTab === 'Pricing Details' && (
          <PricingDetailsSection
            title="Pricing details"
            subtitle="Commercial proposal for this under-consideration lead — unit price through total.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <PricingMetricTile
                label="Unit price"
                value={formatCurrency(leadbyId?.underConsideration?.customer_proposal)}
              />
              <PricingMetricTile
                label="PLC price"
                value={formatCurrency(leadbyId?.underConsideration?.plc_price)}
              />
              <PricingMetricTile
                label="Extra charges"
                value={formatCurrency(leadbyId?.underConsideration?.extra_charges)}
              />
              <PricingMetricTile label="GST" value={formatCurrency(leadbyId?.underConsideration?.gst)} />
              <PricingMetricTile
                label="Total price"
                value={formatCurrency(leadbyId?.underConsideration?.total_price)}
                emphasis
              />
            </div>
          </PricingDetailsSection>
        )}
      </div>

      {/* Extra Charges Breakdown Modal (from close deal extra_charges_snapshot) */}
      {showExtraChargesBreakdownModal && (
        <Modal
          width={45}
          name="Extra Charges Breakdown"
          onClose={() => setShowExtraChargesBreakdownModal(false)}>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-gray-600">
              Total extra charges:{' '}
              {formatCurrency(closeDealData?.extra_charges ?? leadbyId?.deal?.extra_charges ?? 0)}
            </p>
            {closeDealData?.extra_charges_snapshot?.length > 0 ||
            leadbyId?.deal?.extra_charges_snapshot?.length > 0 ? (
              <div className="flex flex-col gap-2">
                {(
                  closeDealData?.extra_charges_snapshot ??
                  leadbyId?.deal?.extra_charges_snapshot ??
                  []
                ).map((item) => {
                  const base = Number(item.total_price) || 0;
                  const gstPct = Number(item.gst) || 0;
                  const gstAmount = (base * gstPct) / 100;
                  const totalWithGst = base + gstAmount;
                  return (
                    <div
                      key={item.id}
                      className="flex justify-between items-start p-3 bg-gray-50 rounded border border-gray-200 text-sm">
                      <span className="font-medium text-gray-900">
                        {item.name || 'Extra charge'}
                      </span>
                      <span className="text-gray-700 text-right">
                        ₹{totalWithGst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}{' '}
                        <span className="text-gray-500">
                          (₹{base.toLocaleString('en-IN')} + ₹
                          {gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} GST)
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No breakdown available.</p>
            )}
          </div>
        </Modal>
      )}

      {/* Follow Up Modal */}
      {showFollowUpModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Follow Up"
          onClose={() => {
            setShowFollowUpModal(false);
            resetFollowUpForm();
          }}>
          <div className="flex flex-col space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Lead Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-2">
              <h3 className="font-semibold text-lg text-gray-800">
                Follow Up with {leadbyId?.user?.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Project: {leadbyId?.projects?.[0]?.name || 'N/A'}
              </p>
            </div>

            {/* Stage Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stage <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name="followUpStage"
                rules={{ required: 'Stage is required' }}
                render={({ field: { onChange, value } }) => (
                  <select
                    value={value || ''}
                    onChange={(e) => {
                      setSelectedStage(e.target.value);
                      setStage(e.target.value);
                      onChange(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="" disabled>
                      Select Stage
                    </option>
                    {stageOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.title}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.followUpStage && (
                <p className="text-red-500 text-sm mt-1">{errors.followUpStage.message}</p>
              )}
            </div>

            {/* Log Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Log <span className="text-red-500">*</span>
              </label>
              <select
                value={log}
                onChange={(e) => {
                  setLog(e.target.value);
                  if (e.target.value !== 'Other') {
                    setOtherLog('');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="" disabled>
                  Select Log
                </option>
                {logOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {log === 'Other' && (
                <input
                  type="text"
                  placeholder="Enter log here..."
                  value={otherLog}
                  onChange={(e) => setOtherLog(e.target.value)}
                  className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>

            {/* Next Follow Up Date */}
            {/* {stage !== 'Deal Closed' && stage !== 'Deal Dropped' && ( */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Follow Up Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {/* )} */}

            {/* Agenda Field */}
            {/* {stage !== 'Deal Closed' && stage !== 'Deal Dropped' && ( */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agenda For Next Follow Up <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Enter agenda here..."
                value={agenda}
                onChange={(e) => setAgenda(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {/* )} */}

            {/* Project Accommodation - Conditional */}
            {(stage === 'Commercials Shared' || stage === 'Negotiation') && (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Accommodation <span className="text-red-500">*</span>
                </label>
                <Multiselect
                  options={projectAccommodationsData || []}
                  selectedValues={projectAccommodations}
                  onSelect={(selectedList) => setProjectAccommodations(selectedList)}
                  onRemove={(selectedList) => setProjectAccommodations(selectedList)}
                  displayValue="name"
                  placeholder={
                    leadbyId?.interested_projects?.[0]
                      ? 'Select project accommodations'
                      : 'Select a project first'
                  }
                  disabled={!leadbyId?.interested_projects?.[0]}
                  showCheckbox
                  style={{
                    multiselectContainer: { width: '100%' },
                    searchBox: {
                      minHeight: '40px',
                      fontSize: '1rem',
                      background: leadbyId?.interested_projects?.[0] ? 'white' : '#f5f5f5'
                    },
                    chips: { fontSize: '0.875rem', padding: '0.25rem 0.5rem' }
                  }}
                />
              </div>
            )}

            {/* File Upload */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachment (Optional)
              </label>
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <input
                    id="file-upload"
                    type="file"
                    className="sr-only"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx"
                    disabled={uploading}
                  />
                  {uploading ? 'Uploading...' : 'Choose File'}
                </label>
                {uploadedImage && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="truncate max-w-xs">{uploadedImage.name}</span>
                    <button
                      onClick={() => setUploadedImage(null)}
                      className="text-red-500 hover:text-red-700">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowFollowUpModal(false);
                  resetFollowUpForm();
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="button" onClick={handleCompleteFollowup}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Update Follow Up Date Modal */}
      {showUpdateFollowUpModal && (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Update Follow Up Date"
          overflowVisible
          onClose={() => {
            setShowUpdateFollowUpModal(false);
            setSelectedFollowUp(null);
            setFollowUpDate('');
          }}>
          <div className="flex flex-col space-y-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowUpdateFollowUpModal(false);
                  setSelectedFollowUp(null);
                  setFollowUpDate('');
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button
                disabled={isUpdatingFollowUp}
                type="button"
                onClick={handleUpdateFollowUpDate}>
                {isUpdatingFollowUp ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Close Deal Modal */}
      {isCloseDealModalOpen && (
        <Modal
          width={50}
          name="Close Deal"
          confirmOnClose={true}
          isDirty={true}
          onClose={() => {
            setIsCloseDealModalOpen(false);
          }}>
          <ClosedWonCloseDeal
            onClose={() => {
              setIsCloseDealModalOpen(false);
            }}
            onSubmit={handleCloseDeal}
            isSubmitting={isSubmitting}
            selectedLead={ID}
            ucDetails={ucDetails}
          />
        </Modal>
      )}

      {/* Under Consideration Modal */}
      {isUnderConsiderationModalOpen && (
        <Modal
          width={50}
          name="Create Under Consideration"
          confirmOnClose={true}
          isDirty={true}
          onClose={() => {
            setIsUnderConsiderationModalOpen(false);
          }}>
          <UnderConsiderationCreate
            onClose={() => {
              setIsUnderConsiderationModalOpen(false);
            }}
            onSubmit={handleUnderConsideration}
            isSubmitting={isSubmitting}
            selectedLead={ID}
          />
        </Modal>
      )}

      {/* Stage Update Modal */}
      {showStageModal && (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Update Stage"
          onClose={() => {
            setShowStageModal(false);
            setStageToUpdate('');
            setProjectAccommodations([]);
          }}>
          <div className="flex flex-col space-y-4">
            {/* Lead Info */}
            <div className="bg-gray-50 p-4 rounded-lg mb-2">
              <h3 className="font-semibold text-lg text-gray-800">
                Update Stage for {leadbyId?.user?.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Current Stage: {leadbyId?.activities?.[0]?.name || 'N/A'}
              </p>
            </div>

            {/* Stage Dropdown */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Stage <span className="text-red-500">*</span>
              </label>
              <select
                value={stageToUpdate}
                onChange={(e) => setStageToUpdate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="" disabled>
                  Select Stage
                </option>
                {stageOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Project Accommodation - Conditional */}
            {(stageToUpdate === 'Commercials Shared' || stageToUpdate === 'Negotiation') && (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Accommodation <span className="text-red-500">*</span>
                </label>
                <Multiselect
                  options={projectAccommodationsData || []}
                  selectedValues={projectAccommodations}
                  onSelect={(selectedList) => setProjectAccommodations(selectedList)}
                  onRemove={(selectedList) => setProjectAccommodations(selectedList)}
                  displayValue="name"
                  placeholder={
                    leadbyId?.interested_projects?.[0]
                      ? 'Select project accommodations'
                      : 'Select a project first'
                  }
                  disabled={!leadbyId?.interested_projects?.[0]}
                  showCheckbox
                  style={{
                    multiselectContainer: { width: '100%' },
                    searchBox: {
                      minHeight: '40px',
                      fontSize: '1rem',
                      background: leadbyId?.interested_projects?.[0] ? 'white' : '#f5f5f5'
                    },
                    chips: { fontSize: '0.875rem', padding: '0.25rem 0.5rem' }
                  }}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowStageModal(false);
                  setStageToUpdate('');
                  setProjectAccommodations([]);
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button disabled={isUpdatingStage} type="button" onClick={handleUpdateStage}>
                {isUpdatingStage ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {isEditLeadModalOpen && (
        <Modal
          width={50}
          confirmOnClose={true}
          isDirty={true}
          name="Edit Lead"
          onClose={() => setIsEditLeadModalOpen(false)}>
          <EditLeadForm
            onSubmit={handleEditLeadSubmit}
            onCancel={() => setIsEditLeadModalOpen(false)}
            isSubmitting={isUpdatingLeadDetails}
            AllProjects={projects}
            editingLead={leadbyId}
          />
        </Modal>
      )}
      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`Cannot delete '${deleteResData?.name || 'this lead'}'`}
          onClose={() => {
            setCanDelete(null);
            setDeleteResData(null);
          }}
          width={45}>
          <div className="flex flex-col justify-center align-center">
            <p className="text-center">
              Looks like this lead has dependencies. Please delete the following stages to proceed.
            </p>
            <div className="flex justify-start align-middle flex-wrap bg-[#f5f7f8] p-2 rounded-[5px] mt-4">
              {deleteResData?.reason?.map((r, index) => (
                <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                  {index + 1}.{r}
                  {index < deleteResData.reason.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          </div>
          <div className="w-full flex justify-end items-center space-x-4 mt-8">
            <Button
              type="button"
              onClick={() => {
                setCanDelete(null);
                setDeleteResData(null);
              }}
              displayType="secondary">
              Close
            </Button>
          </div>
        </Modal>
      )}
      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name="Confirmation"
          onClose={() => {
            setCanDelete(null);
            setDeleteResData(null);
          }}
          width={45}>
          <div className="flex flex-col justify-start items-center z-10">
            <h1 className="text-center text-[1.4rem] font-medium text-primary">
              Are you sure you want to delete the lead &apos;{deleteResData?.name}&apos;?
            </h1>
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => {
                  setCanDelete(null);
                  setDeleteResData(null);
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmDeleteLead}
                disabled={isDeletingLead}
                displayType="primary-red">
                {isDeletingLead ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {isCloneLeadModalOpen && (
        <Modal
          width={40}
          confirmOnClose={true}
          isDirty={true}
          name={`Duplicate Lead '${leadbyId?.user?.name || ''}'`}
          onClose={() => {
            setIsCloneLeadModalOpen(false);
            setSelectedCloneProjects([]);
          }}>
          <div className="flex flex-col justify-between items-start gap-4">
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full">
                <label className="text-gray-600 mb-2 block">Select Projects</label>
                <Multiselect
                  key={`multiselect-clone-${ID}`}
                  options={cloneProjectOptions}
                  disable={!cloneProjectOptions.length}
                  selectedValues={selectedCloneProjects}
                  onSelect={(selectedList) => setSelectedCloneProjects(selectedList)}
                  onRemove={(selectedList) => setSelectedCloneProjects(selectedList)}
                  displayValue="name"
                  placeholder="Select projects"
                  emptyRecordMsg="No projects available"
                  showCheckbox
                  style={{
                    optionContainer: { border: '1px solid', backgroundColor: 'white' },
                    option: { color: 'black', backgroundColor: 'white' },
                    chips: { background: '#2A565E', color: 'white' }
                  }}
                />
              </div>
              <div className="flex justify-end items-center space-x-4 mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setIsCloneLeadModalOpen(false);
                    setSelectedCloneProjects([]);
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmitCloneLead}
                  disabled={isCloningLead || selectedCloneProjects.length === 0}>
                  {isCloningLead ? 'Duplicating...' : 'Duplicate Lead'}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SaleOverview;
